import React, { useState, useCallback, useRef } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import {
  Pencil,
  X,
  Check,
  Trash2,
  Image,
  Video,
  File,
  Bold,
  Italic,
  Strikethrough,
  Upload,
  Loader,
  Phone,
  ExternalLink,
  MessageSquare,
  ArrowRight,
  Variable,
  AlertTriangle,
} from "lucide-react";
import { uploadDocumentFile } from "../../apis/documents";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";
import { getUnknownVariableNames, getVariableNamesFromText } from "../../utils/flowVariableValidation";

/**
 * Build a WhatsApp interactive-message payload from the TemplateNode form data.
 * Uses the "interactive" → "button" shape so NO approved template name or
 * language is required.  Variable placeholders like {{var_name}} inside body
 * and header text are preserved — the backend resolves them at send time.
 *
 * Resulting shape matches:
 *   POST /{phoneNumberId}/messages
 *   { type: "interactive", interactive: <this object> }
 *
 * @param {Object} form  - The ExpandedEditor form state
 * @returns {Object} WhatsApp interactive object (type, header, body, footer, action)
 */
function buildWaTemplate(form) {
  const interactive = {
    type: "button",
  };

  // ── Header (image / video / document / text) ──
  const headerFormat = form.header?.format || "NONE";
  if (headerFormat === "IMAGE" && form.header?.mediaUrl) {
    interactive.header = {
      type: "image",
      image: { link: form.header.mediaUrl },
    };
  } else if (headerFormat === "VIDEO" && form.header?.mediaUrl) {
    interactive.header = {
      type: "video",
      video: { link: form.header.mediaUrl },
    };
  } else if (headerFormat === "DOCUMENT" && form.header?.mediaUrl) {
    interactive.header = {
      type: "document",
      document: {
        link: form.header.mediaUrl,
        filename: form.header.fileName || "document",
      },
    };
  } else if (headerFormat === "TEXT" && form.header?.text) {
    interactive.header = {
      type: "text",
      text: form.header.text, // {{var}} placeholders resolved by backend
    };
  }

  // ── Body ──
  const bodyText = form.body?.text || "";
  if (bodyText) {
    interactive.body = {
      text: bodyText, // {{var}} placeholders resolved by backend
    };
  }

  // ── Footer ──
  const footerText = form.footer?.text || "";
  if (footerText) {
    interactive.footer = {
      text: footerText,
    };
  }

  // ── Action → reply buttons (max 3 per WhatsApp rules) ──
  // Only QUICK_REPLY buttons map to interactive reply buttons.
  // URL and PHONE_NUMBER buttons are not supported in interactive messages.
  const replyButtons = (form.buttons || [])
    .filter((btn) => btn.type === "QUICK_REPLY")
    .slice(0, 3)
    .map((btn) => ({
      type: "reply",
      reply: {
        id: btn.id || btn.text?.toLowerCase().replace(/\s+/g, "_") || `btn_${Date.now()}`,
        title: btn.text || "Button",
      },
    }));

  if (replyButtons.length > 0) {
    interactive.action = {
      buttons: replyButtons,
    };
  }

  return interactive;
}

// ─── WhatsApp Collapsed View (Message Bubble) ─────────────────────
const CollapsedView = ({ data, onEdit }) => {
  const headerFormat = data.header?.format || "NONE";
  const bodyText = data.body?.text || "";
  const footerText = data.footer?.text || "";
  const buttons = data.buttons || [];

  // Format body for display
  let displayBody = bodyText;
  displayBody = displayBody
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
    .replace(/_(.*?)_/g, "<em>$1</em>")
    .replace(/~(.*?)~/g, "<del>$1</del>");

  return (
    <div
      className="relative rounded-xl overflow-hidden"
      style={{ backgroundColor: "#EFEAE2", padding: "10px 10px 6px", minWidth: "260px", maxWidth: "300px" }}
    >
      {/* Edit overlay button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg"
        title="Edit template"
      >
        <Pencil size={12} className="text-[#008069]" />
      </button>

      {/* WhatsApp message bubble */}
      <div className="bg-white rounded-lg shadow-sm relative">
        {/* Tail */}
        <div className="absolute -left-[6px] top-0 w-3 h-3 overflow-hidden">
          <div className="absolute bg-white w-3 h-3 transform rotate-45 translate-x-[6px] shadow-sm" />
        </div>

        <div className="px-2 py-1.5">
          {/* Header */}
          {headerFormat === "TEXT" && data.header?.text && (
            <div className="mb-1 -mx-2 -mt-1.5 px-2 py-1.5 bg-[#F0F0F0] rounded-t-lg">
              <p className="text-[12px] font-semibold text-[#111B21] leading-tight">
                {data.header.text}
              </p>
            </div>
          )}
          {headerFormat === "IMAGE" && (
            <div className="-mx-2 -mt-1.5 rounded-t-lg overflow-hidden bg-[#F0F0F0] flex items-center justify-center mb-1" style={{ maxHeight: 120 }}>
              {data.header?.mediaUrl && data.header.mediaUrl.includes("{{") ? (
                <div className="py-4 text-center">
                  <Image size={24} className="text-[#008069] mx-auto mb-1" />
                  <p className="text-[9px] font-semibold text-[#008069]">
                    From {getVariableNamesFromText(data.header.mediaUrl).map(v => `{{${v}}}`).join(", ") || "variable"}
                  </p>
                </div>
              ) : data.header?.mediaUrl ? (
                <img src={data.header.mediaUrl} alt="" className="w-full object-cover" style={{ maxHeight: 120 }} />
              ) : (
                <div className="py-6 text-center">
                  <Image size={24} className="text-gray-300 mx-auto" />
                  <p className="text-[9px] text-gray-400 mt-1">Image</p>
                </div>
              )}
            </div>
          )}
          {headerFormat === "VIDEO" && (
            <div className="-mx-2 -mt-1.5 rounded-t-lg overflow-hidden bg-black flex items-center justify-center mb-1" style={{ maxHeight: 120 }}>
              {data.header?.mediaUrl && data.header.mediaUrl.includes("{{") ? (
                <div className="py-4 text-center">
                  <Video size={24} className="text-[#008069] mx-auto mb-1" />
                  <p className="text-[9px] font-semibold text-[#008069]">
                    From {getVariableNamesFromText(data.header.mediaUrl).map(v => `{{${v}}}`).join(", ") || "variable"}
                  </p>
                </div>
              ) : data.header?.mediaUrl ? (
                <video src={data.header.mediaUrl} className="w-full object-cover" style={{ maxHeight: 120 }} />
              ) : (
                <div className="py-6 text-center">
                  <Video size={24} className="text-white/30 mx-auto" />
                  <p className="text-[9px] text-white/30 mt-1">Video</p>
                </div>
              )}
            </div>
          )}
          {headerFormat === "DOCUMENT" && (
            <div className="-mx-2 -mt-1.5 px-2 py-2 bg-[#F0F0F0] rounded-t-lg flex items-center gap-2 mb-1">
              <div className="p-1.5 bg-white rounded">
                <File size={14} className={data.header?.mediaUrl && data.header.mediaUrl.includes("{{") ? "text-[#008069]" : "text-red-500"} />
              </div>
              <span className="text-[11px] font-medium truncate flex-1" style={{ color: data.header?.mediaUrl && data.header.mediaUrl.includes("{{") ? "#008069" : "#111B21" }}>
                {data.header?.mediaUrl && data.header.mediaUrl.includes("{{")
                  ? `From ${getVariableNamesFromText(data.header.mediaUrl).map(v => `{{${v}}}`).join(", ") || "variable"}`
                  : (data.header?.fileName || "Document")}
              </span>
            </div>
          )}

          {/* Body */}
          {displayBody ? (
            <div
              className="text-[12px] text-[#111B21] whitespace-pre-wrap break-words leading-[17px]"
              dangerouslySetInnerHTML={{
                __html: displayBody.length > 120
                  ? displayBody.substring(0, 120) + "..."
                  : displayBody,
              }}
            />
          ) : (
            <p className="text-[12px] text-gray-400 italic">Type a message...</p>
          )}

          {/* Footer */}
          {footerText && (
            <p className="text-[10px] text-[#667781] mt-1 leading-tight">{footerText}</p>
          )}

          {/* Timestamp */}
          <div className="flex items-center justify-end mt-0.5 -mb-0.5">
            <span className="text-[9px] text-[#667781]">
              {new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}
            </span>
          </div>
        </div>

        {/* Buttons */}
        {buttons.length > 0 && (
          <div className="border-t border-gray-100">
            {buttons.map((btn, i) => {
              const icon =
                btn.type === "URL" ? <ExternalLink size={12} className="text-[#027EB5]" /> :
                btn.type === "PHONE_NUMBER" ? <Phone size={12} className="text-[#027EB5]" /> :
                <MessageSquare size={12} className="text-[#027EB5]" />;

              return (
                <div
                  key={btn.id}
                  className={`relative flex items-center justify-center gap-1.5 py-[7px] ${
                    i < buttons.length - 1 ? "border-b border-gray-100" : ""
                  }`}
                >
                  {icon}
                  <span className="text-[12px] font-medium text-[#027EB5]">
                    {btn.text || `Button ${i + 1}`}
                  </span>
                  {/* Connection dot (QUICK_REPLY only) */}
                  {btn.type === "QUICK_REPLY" && btn.connectedNodeId && (
                    <div
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#008069] rounded-full border border-white shadow-sm"
                      title={`Connected to: ${btn.connectedNodeId}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Button labels under node for connection handles (QUICK_REPLY + Other) */}
      {buttons.filter((btn) => btn.type === "QUICK_REPLY").length > 0 && (
        <div className="absolute -right-16 top-0 h-full flex flex-col justify-around items-start py-4 pointer-events-none">
          {buttons.filter((btn) => btn.type === "QUICK_REPLY").map((btn) => (
            <span
              key={btn.id}
              className="text-[7px] text-[#667781] font-semibold truncate max-w-[60px] text-center"
              title={btn.text || btn.type}
            >
              {btn.text || btn.type}
            </span>
          ))}
          <span
            key="other-label"
            className="text-[7px] text-[#F59E0B] font-semibold truncate max-w-[60px] text-center"
            title="Other (no button match)"
          >
            Other
          </span>
        </div>
      )}
    </div>
  );
};

// ─── WhatsApp-Style Expanded Editor (Named Variables) ─────────────
const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable, nodeId }) => {
  const [form, setForm] = useState(() => ({
    header: {
      format: data.header?.format || "NONE",
      text: data.header?.text || "",
      mediaUrl: data.header?.mediaUrl || "",
      fileName: data.header?.fileName || "",
    },
    body: {
      text: data.body?.text || "",
      variables: data.body?.variables ? data.body.variables.map((v) => ({ ...v })) : [],
    },
    footer: {
      text: data.footer?.text || "",
    },
    buttons: data.buttons ? data.buttons.map((b) => ({ ...b })) : [],
  }));

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const bodyRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaUrlRef = useRef(null);

  // ── helpers ──
  const updateField = (path, value) => {
    setForm((prev) => {
      const parts = path.split(".");
      const next = { ...prev };
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) {
        obj[parts[i]] = { ...obj[parts[i]] };
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
  };

  // ── file upload ──
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError("");
    setUploading(true);
    console.log("file", file);
    try {
      const response = await uploadDocumentFile(file);
      const mediaUrl = response.data.data.doc_url;
      updateField("header.mediaUrl", mediaUrl);
      updateField("header.fileName", file.name);
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeMedia = () => {
    updateField("header.mediaUrl", "");
    updateField("header.fileName", "");
  };

  // ── media URL variable helper ──
  const insertVariableInMediaUrl = (varName) => {
    if (!varName) return;
    const placeholder = `{{${varName}}}`;
    const input = mediaUrlRef.current;
    const currentUrl = form.header.mediaUrl || "";
    if (input) {
      const pos = input.selectionStart ?? currentUrl.length;
      const before = currentUrl.slice(0, pos);
      const after = currentUrl.slice(pos);
      updateField("header.mediaUrl", `${before}${placeholder}${after}`);
    } else {
      updateField("header.mediaUrl", currentUrl + placeholder);
    }
  };

  const getAcceptType = () => {
    switch (form.header.format) {
      case "IMAGE": return "image/png,image/jpeg,image/jpg,image/webp";
      case "VIDEO": return "video/mp4,video/avi,video/webm";
      case "DOCUMENT": return ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx";
      default: return "*";
    }
  };

  // ── body variable helpers (named only) ──
  const insertVariable = (varName) => {
    if (!varName) return;
    // Add to body variables if not already present
    const exists = form.body.variables.some((v) => v.name === varName);
    if (!exists) {
      const newVars = [...form.body.variables, { name: varName, example: "" }];
      updateField("body.variables", newVars);
    }
    // Insert {{variable_name}} at cursor
    const textarea = bodyRef.current;
    const placeholder = `{{${varName}}}`;
    if (textarea) {
      const pos = textarea.selectionStart || form.body.text.length;
      const before = form.body.text.slice(0, pos);
      const after = form.body.text.slice(pos);
      updateField("body.text", `${before}${placeholder}${after}`);
    } else {
      updateField("body.text", form.body.text + placeholder);
    }
  };

  const updateVariableExample = (name, example) => {
    const vars = form.body.variables.map((v) =>
      v.name === name ? { ...v, example } : v
    );
    updateField("body.variables", vars);
  };

  const removeBodyVariable = (name) => {
    const vars = form.body.variables.filter((v) => v.name !== name);
    updateField("body.variables", vars);
    // Remove all occurrences of {{name}} from body text
    const text = form.body.text.replace(new RegExp(`\\{\\{${name}\\}\\}`, "g"), "");
    updateField("body.text", text);
  };

  // ── button helpers ──
  const addButton = (type) => {
    const btns = form.buttons;
    if (btns.length >= 6) return;
    const qr = btns.filter((b) => b.type === "QUICK_REPLY").length;
    const url = btns.filter((b) => b.type === "URL").length;
    const ph = btns.filter((b) => b.type === "PHONE_NUMBER").length;
    if (type === "QUICK_REPLY" && qr >= 3) return;
    if (type === "URL" && url >= 2) return;
    if (type === "PHONE_NUMBER" && ph >= 1) return;

    const newBtn = {
      type,
      text: "",
      id: `btn_${Date.now()}`,
      connectedNodeId: null,
      selectionVariable: "",
      ...(type === "URL" && { url: "", urlVariables: [] }),
      ...(type === "PHONE_NUMBER" && { phone_number: "" }),
    };
    updateField("buttons", [...btns, newBtn]);
  };

  const updateButton = (id, field, value) => {
    const btns = form.buttons.map((b) =>
      b.id === id ? { ...b, [field]: value } : b
    );
    updateField("buttons", btns);
  };

  const removeButton = (id) => {
    updateField("buttons", form.buttons.filter((b) => b.id !== id));
  };

  // ── formatting ──
  const applyFormat = (format) => {
    const textarea = bodyRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = form.body.text;
    const selected = text.substring(start, end);
    const markers = { bold: "*", italic: "_", strikethrough: "~" };
    const m = markers[format] || "*";
    const wrapped = selected ? `${m}${selected}${m}` : `${m}text${m}`;
    const newText = text.substring(0, start) + wrapped + text.substring(end);
    updateField("body.text", newText);
  };

  // ── live preview body ──
  const getPreviewBody = () => {
    let preview = form.body.text || "";
    (form.body.variables || []).forEach((v) => {
      preview = preview.replace(
        new RegExp(`\\{\\{${v.name}\\}\\}`, "g"),
        v.example || `{{${v.name}}}`
      );
    });
    preview = preview
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
      .replace(/_(.*?)_/g, "<em>$1</em>")
      .replace(/~(.*?)~/g, "<del>$1</del>");
    return preview;
  };

  const handleSave = () => {
    const waTemplate = buildWaTemplate(form);
    onSave({ ...form, waTemplate });
  };

  // Variable insertion dropdown state
  const [showVarPicker, setShowVarPicker] = useState(false);

  return (
    <div
      className="nowheel"
      style={{ backgroundColor: "#EFEAE2", borderRadius: "12px", padding: "14px", minWidth: "380px", maxWidth: "420px" }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#008069]" />
          <span className="text-[13px] font-bold text-[#111B21]">Edit Template</span>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={handleSave}
            className="px-3 py-1.5 text-[11px] font-bold bg-[#008069] text-white rounded-lg hover:bg-[#006e5a] transition-colors flex items-center gap-1 shadow-sm"
          >
            <Check size={12} />
            Save
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-[11px] font-bold bg-white text-[#667781] rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* ── Message bubble editing area ── */}
      <NodeEditorTabs nodeType="template">
      <div className="bg-white rounded-lg shadow-sm mb-3 relative">
        {/* Tail */}
        <div className="absolute -left-[6px] top-0 w-3 h-3 overflow-hidden">
          <div className="absolute bg-white w-3 h-3 transform rotate-45 translate-x-[6px] shadow-sm" />
        </div>

        {/* Header section */}
        <div className="px-3 pt-2.5 pb-1">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-bold text-[#667781] uppercase tracking-wider">Header</span>
            <select
              value={form.header.format}
              onChange={(e) => {
                updateField("header.format", e.target.value);
                if (e.target.value === "NONE" || e.target.value === "TEXT") {
                  updateField("header.mediaUrl", "");
                  updateField("header.fileName", "");
                }
              }}
              className="text-[10px] font-semibold text-[#008069] bg-[#E7FCE8] px-2 py-0.5 rounded-full border-0 focus:outline-none cursor-pointer"
            >
              <option value="NONE">None</option>
              <option value="TEXT">Text</option>
              <option value="IMAGE">Image</option>
              <option value="VIDEO">Video</option>
              <option value="DOCUMENT">Document</option>
            </select>
          </div>

          {form.header.format === "TEXT" && (
            <input
              value={form.header.text}
              onChange={(e) => updateField("header.text", e.target.value)}
              placeholder="Header text..."
              maxLength={60}
              className="w-full px-0 py-1 text-[13px] font-semibold text-[#111B21] border-0 border-b border-gray-200 focus:border-[#008069] focus:outline-none bg-transparent placeholder:text-gray-400"
            />
          )}

          {["IMAGE", "VIDEO", "DOCUMENT"].includes(form.header.format) && (
            <div className="mb-1 space-y-1.5">
              {/* Media URL input with Insert variable */}
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <input
                    ref={mediaUrlRef}
                    type="text"
                    value={form.header.mediaUrl}
                    onChange={(e) => updateField("header.mediaUrl", e.target.value)}
                    placeholder={`https://... or {{variable}}`}
                    className="flex-1 px-2 py-1.5 text-[11px] text-[#111B21] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-[#008069] placeholder:text-gray-400 font-medium"
                  />
                  <VariableDropdown
                    value=""
                    onChange={(name) => { if (name) insertVariableInMediaUrl(name); }}
                    flowVariables={flowVariables || []}
                    allowCreate
                    onCreateVariable={onCreateVariable}
                    placeholder="Var"
                    className="w-20"
                  />
                  {form.header.mediaUrl && (
                    <button
                      onClick={removeMedia}
                      className="p-1 text-[#667781] hover:text-red-500 rounded transition-colors flex-shrink-0"
                      title="Clear media URL"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                <p className="text-[9px] text-[#8696a0]">
                  Paste a URL, use {"{{variable}}"}, or upload a file below.
                </p>
              </div>

              {/* Upload button (compact) */}
              <label className="block cursor-pointer">
                <div
                  className={`rounded-lg p-2 text-center transition-all ${
                    uploading
                      ? "bg-[#E7FCE8] border border-[#008069]/30"
                      : "bg-[#F0F0F0] hover:bg-[#E8E8E8] border border-transparent"
                  }`}
                >
                  {uploading ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader size={14} className="text-[#008069] animate-spin" />
                      <span className="text-[10px] text-[#008069] font-semibold">Uploading...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-1.5">
                      <Upload size={14} className="text-[#667781]" />
                      <span className="text-[10px] font-medium text-[#667781]">
                        Upload {form.header.format.toLowerCase()}
                      </span>
                      <span className="text-[8px] text-[#8696a0]">
                        {form.header.format === "IMAGE" && "(PNG, JPG)"}
                        {form.header.format === "VIDEO" && "(MP4)"}
                        {form.header.format === "DOCUMENT" && "(PDF, DOC)"}
                      </span>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept={getAcceptType()}
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
              </label>

              {/* Preview (only when URL is set and does not contain variables) */}
              {form.header.mediaUrl && !form.header.mediaUrl.includes("{{") && (
                <div className="relative">
                  {form.header.format === "IMAGE" && (
                    <div className="relative rounded-lg overflow-hidden">
                      <img src={form.header.mediaUrl} alt="Header" className="w-full max-h-32 object-cover rounded-lg" />
                    </div>
                  )}
                  {form.header.format === "VIDEO" && (
                    <div className="relative rounded-lg overflow-hidden bg-black">
                      <video src={form.header.mediaUrl} className="w-full max-h-32 object-cover rounded-lg" controls />
                    </div>
                  )}
                  {form.header.format === "DOCUMENT" && (
                    <div className="flex items-center gap-2 p-2 bg-[#F0F0F0] rounded-lg">
                      <div className="p-1.5 bg-white rounded">
                        <File size={14} className="text-red-500" />
                      </div>
                      <span className="text-[11px] text-[#111B21] font-medium truncate flex-1">
                        {form.header.fileName || "Document"}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Variable hint when URL contains placeholders */}
              {form.header.mediaUrl && form.header.mediaUrl.includes("{{") && (() => {
                const varNames = getVariableNamesFromText(form.header.mediaUrl);
                const varLabel = varNames.length > 0
                  ? varNames.map(v => `{{${v}}}`).join(", ")
                  : "variables";
                return (
                  <div className="flex items-center gap-1.5 px-2 py-1.5 bg-[#E7FCE8] rounded-lg">
                    <Variable size={10} className="text-[#008069] flex-shrink-0" />
                    <span className="text-[9px] text-[#008069] font-medium">
                      {form.header.format === "IMAGE" ? "Image" : form.header.format === "VIDEO" ? "Video" : "Document"} from {varLabel} — resolved at send time.
                    </span>
                  </div>
                );
              })()}

              {uploadError && (
                <p className="text-[10px] text-red-600 font-medium mt-1">{uploadError}</p>
              )}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="mx-3 border-t border-gray-100" />

        {/* Body section */}
        <div className="px-3 py-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold text-[#667781] uppercase tracking-wider">Body</span>
            <div className="flex items-center gap-0.5">
              <button onClick={() => applyFormat("bold")} className="p-1 hover:bg-[#F0F0F0] rounded" title="Bold">
                <Bold size={11} className="text-[#667781]" />
              </button>
              <button onClick={() => applyFormat("italic")} className="p-1 hover:bg-[#F0F0F0] rounded" title="Italic">
                <Italic size={11} className="text-[#667781]" />
              </button>
              <button onClick={() => applyFormat("strikethrough")} className="p-1 hover:bg-[#F0F0F0] rounded" title="Strikethrough">
                <Strikethrough size={11} className="text-[#667781]" />
              </button>
              <div className="w-px h-3 bg-gray-200 mx-0.5" />
              <div className="relative">
                <button
                  onClick={() => setShowVarPicker(!showVarPicker)}
                  className="text-[9px] px-1.5 py-0.5 bg-[#008069] text-white rounded-full font-semibold hover:bg-[#006e5a] flex items-center gap-0.5"
                >
                  <Variable size={8} />
                  Var
                </button>
                {showVarPicker && (
                  <div className="absolute right-0 top-full mt-1 z-50 w-48">
                    <VariableDropdown
                      value=""
                      onChange={(name) => {
                        if (name) insertVariable(name);
                        setShowVarPicker(false);
                      }}
                      flowVariables={flowVariables || []}
                      allowCreate
                      onCreateVariable={onCreateVariable}
                      placeholder="Pick variable to insert..."
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <textarea
            ref={bodyRef}
            value={form.body.text}
            onChange={(e) => updateField("body.text", e.target.value)}
            placeholder="Type a message..."
            rows={3}
            maxLength={1024}
            className="w-full px-0 py-1 text-[12px] text-[#111B21] border-0 focus:outline-none resize-none bg-transparent placeholder:text-[#8696a0] leading-[17px]"
          />
          <div className="flex justify-between">
            <span className="text-[9px] text-[#8696a0]">*bold* _italic_ ~strike~</span>
            <span className="text-[9px] text-[#8696a0]">{form.body.text.length}/1024</span>
          </div>

          {/* Body variable validation warning */}
          {(() => {
            const unknownBodyVars = getUnknownVariableNames(form.body.text, flowVariables);
            if (unknownBodyVars.length === 0) return null;
            return (
              <div className="flex items-start gap-1 mt-1 px-2 py-1 bg-amber-50 rounded text-[9px] text-amber-700">
                <AlertTriangle size={10} className="flex-shrink-0 mt-0.5" />
                <span>Unknown variable(s): <strong>{unknownBodyVars.join(", ")}</strong>. Add them in Variables.</span>
              </div>
            );
          })()}

          {/* Named variable examples */}
          {form.body.variables.length > 0 && (
            <div className="mt-1.5 space-y-1 p-2 bg-[#F0F6F0] rounded-lg">
              <span className="text-[9px] font-bold text-[#008069] uppercase tracking-wider">Variable Examples</span>
              {form.body.variables.map((v) => (
                <div key={v.name} className="flex items-center gap-1">
                  <span className="text-[10px] font-mono font-bold text-[#008069] min-w-[60px] truncate">{`{{${v.name}}}`}</span>
                  <input
                    type="text"
                    value={v.example}
                    onChange={(e) => updateVariableExample(v.name, e.target.value)}
                    placeholder="sample value"
                    className="flex-1 px-1.5 py-0.5 text-[10px] bg-white border border-[#008069]/20 rounded focus:border-[#008069] focus:outline-none"
                  />
                  <button onClick={() => removeBodyVariable(v.name)} className="p-0.5 text-red-400 hover:text-red-600 rounded">
                    <X size={9} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="mx-3 border-t border-gray-100" />

        {/* Footer section */}
        <div className="px-3 py-2">
          <span className="text-[10px] font-bold text-[#667781] uppercase tracking-wider">Footer</span>
          <input
            type="text"
            value={form.footer.text}
            onChange={(e) => updateField("footer.text", e.target.value)}
            placeholder="Optional footer text..."
            maxLength={60}
            className="w-full px-0 py-1 text-[11px] text-[#667781] border-0 focus:outline-none bg-transparent placeholder:text-[#8696a0]"
          />
        </div>
      </div>

      {/* ── Buttons section ── */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold text-[#667781] uppercase tracking-wider">
            Buttons ({form.buttons.length}/6)
          </span>
        </div>

        {/* Add button row */}
        <div className="flex gap-1.5 mb-2">
          <button
            onClick={() => addButton("QUICK_REPLY")}
            disabled={form.buttons.length >= 6 || form.buttons.filter((b) => b.type === "QUICK_REPLY").length >= 3}
            className="text-[10px] px-2.5 py-1 bg-white text-[#027EB5] border border-[#027EB5]/20 rounded-full font-semibold hover:bg-[#027EB5]/5 disabled:opacity-30 flex items-center gap-1 transition-colors"
          >
            <MessageSquare size={9} />
            Reply
          </button>
          <button
            onClick={() => addButton("URL")}
            disabled={form.buttons.length >= 6 || form.buttons.filter((b) => b.type === "URL").length >= 2}
            className="text-[10px] px-2.5 py-1 bg-white text-[#027EB5] border border-[#027EB5]/20 rounded-full font-semibold hover:bg-[#027EB5]/5 disabled:opacity-30 flex items-center gap-1 transition-colors"
          >
            <ExternalLink size={9} />
            URL
          </button>
          <button
            onClick={() => addButton("PHONE_NUMBER")}
            disabled={form.buttons.length >= 6 || form.buttons.filter((b) => b.type === "PHONE_NUMBER").length >= 1}
            className="text-[10px] px-2.5 py-1 bg-white text-[#027EB5] border border-[#027EB5]/20 rounded-full font-semibold hover:bg-[#027EB5]/5 disabled:opacity-30 flex items-center gap-1 transition-colors"
          >
            <Phone size={9} />
            Phone
          </button>
        </div>

        {/* Button list - WhatsApp style */}
        {form.buttons.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {form.buttons.map((btn, i) => {
              const icon =
                btn.type === "URL" ? <ExternalLink size={13} className="text-[#027EB5]" /> :
                btn.type === "PHONE_NUMBER" ? <Phone size={13} className="text-[#027EB5]" /> :
                <MessageSquare size={13} className="text-[#027EB5]" />;

              return (
                <div
                  key={btn.id}
                  className={`p-2.5 ${i < form.buttons.length - 1 ? "border-b border-gray-100" : ""}`}
                >
                  {/* Button header: type + remove */}
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                      {icon}
                      <span className="text-[10px] font-bold text-[#027EB5]">
                        {btn.type === "QUICK_REPLY" ? "Quick Reply" : btn.type === "URL" ? "URL Button" : "Call Button"}
                      </span>
                    </div>
                    <button onClick={() => removeButton(btn.id)} className="p-0.5 text-[#667781] hover:text-red-500 rounded transition-colors">
                      <Trash2 size={11} />
                    </button>
                  </div>

                  {/* Button text input */}
                  <input
                    type="text"
                    value={btn.text}
                    onChange={(e) => updateButton(btn.id, "text", e.target.value)}
                    placeholder="Button label"
                    maxLength={20}
                    className="w-full px-2 py-1.5 text-[11px] text-[#111B21] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-[#008069]/30 placeholder:text-[#8696a0]"
                  />

                  {/* URL input with variable support */}
                  {btn.type === "URL" && (
                    <div className="mt-1">
                      <input
                        type="text"
                        value={btn.url || ""}
                        onChange={(e) => updateButton(btn.id, "url", e.target.value)}
                        placeholder="https://example.com/order/{{order_id}}"
                        className="w-full px-2 py-1.5 text-[11px] text-[#111B21] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-[#008069]/30 placeholder:text-[#8696a0] font-mono"
                      />
                      <p className="text-[8px] text-[#8696a0] mt-0.5">Use {`{{variable_name}}`} for dynamic URLs</p>

                      {/* URL variable validation warning */}
                      {(() => {
                        const unknownUrlVars = getUnknownVariableNames(btn.url || "", flowVariables);
                        if (unknownUrlVars.length === 0) return null;
                        return (
                          <div className="flex items-start gap-1 mt-1 px-2 py-0.5 bg-amber-50 rounded text-[8px] text-amber-700">
                            <AlertTriangle size={8} className="flex-shrink-0 mt-0.5" />
                            <span>Unknown: <strong>{unknownUrlVars.join(", ")}</strong></span>
                          </div>
                        );
                      })()}

                      {/* URL variable examples */}
                      {(() => {
                        const urlVars = (btn.url || "").match(/\{\{(\w+)\}\}/g);
                        if (!urlVars || urlVars.length === 0) return null;
                        const varNames = urlVars.map((v) => v.replace(/\{\{|\}\}/g, ""));
                        return (
                          <div className="mt-1 space-y-0.5 p-1.5 bg-[#F0F6F0] rounded">
                            <span className="text-[8px] font-bold text-[#008069] uppercase">URL Variable Examples</span>
                            {varNames.map((name) => (
                              <div key={name} className="flex items-center gap-1">
                                <span className="text-[9px] font-mono font-bold text-[#008069] min-w-[50px]">{`{{${name}}}`}</span>
                                <input
                                  type="text"
                                  value={(btn.urlVariableExamples || {})[name] || ""}
                                  onChange={(e) => {
                                    const examples = { ...(btn.urlVariableExamples || {}), [name]: e.target.value };
                                    updateButton(btn.id, "urlVariableExamples", examples);
                                  }}
                                  placeholder="example value"
                                  className="flex-1 px-1 py-0.5 text-[9px] bg-white border border-[#008069]/20 rounded focus:outline-none"
                                />
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Phone input */}
                  {btn.type === "PHONE_NUMBER" && (
                    <input
                      type="tel"
                      value={btn.phone_number || ""}
                      onChange={(e) => updateButton(btn.id, "phone_number", e.target.value)}
                      placeholder="+1234567890"
                      className="w-full mt-1 px-2 py-1.5 text-[11px] text-[#111B21] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-[#008069]/30 placeholder:text-[#8696a0]"
                    />
                  )}

                  {/* Store button selection in variable */}
                  {btn.type === "QUICK_REPLY" && (
                    <div className="mt-1.5">
                      <label className="text-[8px] font-bold text-[#667781] uppercase tracking-wider mb-0.5 block">
                        Store selection in variable
                      </label>
                      <VariableDropdown
                        value={btn.selectionVariable || ""}
                        onChange={(name) => updateButton(btn.id, "selectionVariable", name)}
                        flowVariables={flowVariables || []}
                        allowCreate
                        onCreateVariable={onCreateVariable}
                        placeholder="Optional: save click in variable"
                      />
                    </div>
                  )}

                  {/* Connection indicator (QUICK_REPLY only) */}
                  {btn.type === "QUICK_REPLY" && btn.connectedNodeId && (
                    <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 bg-[#E7FCE8] rounded-lg">
                      <ArrowRight size={10} className="text-[#008069]" />
                      <span className="text-[9px] text-[#008069] font-semibold">
                        Connected to node
                      </span>
                      <div className="w-1.5 h-1.5 rounded-full bg-[#008069] animate-pulse" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {form.buttons.length === 0 && (
          <div className="text-center py-3 bg-white/60 rounded-lg">
            <p className="text-[10px] text-[#8696a0]">No buttons added yet</p>
          </div>
        )}
      </div>

      {/* ── Live WhatsApp Preview ── */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="px-3 py-1.5 bg-[#008069] flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-white/60" />
          <span className="text-[9px] font-bold text-white uppercase tracking-wider">Live Preview</span>
        </div>
        <div className="p-3" style={{ backgroundColor: "#EFEAE2" }}>
          <div className="bg-white rounded-lg shadow-sm relative max-w-[250px]">
            {/* Tail */}
            <div className="absolute -left-[5px] top-0 w-2.5 h-2.5 overflow-hidden">
              <div className="absolute bg-white w-2.5 h-2.5 transform rotate-45 translate-x-[5px] shadow-sm" />
            </div>
            <div className="px-2 py-1.5">
              {/* Preview Header */}
              {form.header.format === "TEXT" && form.header.text && (
                <div className="-mx-2 -mt-1.5 px-2 py-1 bg-[#F0F0F0] rounded-t-lg mb-1">
                  <p className="text-[11px] font-semibold text-[#111B21]">{form.header.text}</p>
                </div>
              )}
              {form.header.format === "IMAGE" && (
                <div className="-mx-2 -mt-1.5 rounded-t-lg overflow-hidden bg-[#F0F0F0] mb-1" style={{ maxHeight: 80 }}>
                  {form.header.mediaUrl && form.header.mediaUrl.includes("{{") ? (
                    <div className="py-3 text-center">
                      <Image size={18} className="text-[#008069] mx-auto mb-0.5" />
                      <p className="text-[8px] font-semibold text-[#008069]">
                        From {getVariableNamesFromText(form.header.mediaUrl).map(v => `{{${v}}}`).join(", ") || "variable"}
                      </p>
                    </div>
                  ) : form.header.mediaUrl ? (
                    <img src={form.header.mediaUrl} alt="" className="w-full object-cover" style={{ maxHeight: 80 }} />
                  ) : (
                    <div className="py-4 flex items-center justify-center">
                      <Image size={18} className="text-gray-300" />
                    </div>
                  )}
                </div>
              )}
              {form.header.format === "VIDEO" && (
                <div className="-mx-2 -mt-1.5 rounded-t-lg overflow-hidden bg-black mb-1" style={{ maxHeight: 80 }}>
                  {form.header.mediaUrl && form.header.mediaUrl.includes("{{") ? (
                    <div className="py-3 text-center">
                      <Video size={18} className="text-[#008069] mx-auto mb-0.5" />
                      <p className="text-[8px] font-semibold text-[#008069]">
                        From {getVariableNamesFromText(form.header.mediaUrl).map(v => `{{${v}}}`).join(", ") || "variable"}
                      </p>
                    </div>
                  ) : form.header.mediaUrl ? (
                    <video src={form.header.mediaUrl} className="w-full object-cover" style={{ maxHeight: 80 }} />
                  ) : (
                    <div className="py-4 flex items-center justify-center">
                      <Video size={18} className="text-white/30" />
                    </div>
                  )}
                </div>
              )}
              {form.header.format === "DOCUMENT" && (
                <div className="-mx-2 -mt-1.5 px-2 py-1.5 bg-[#F0F0F0] rounded-t-lg flex items-center gap-1.5 mb-1">
                  <File size={12} className={form.header.mediaUrl && form.header.mediaUrl.includes("{{") ? "text-[#008069]" : "text-red-500"} />
                  <span className={`text-[10px] font-medium truncate ${form.header.mediaUrl && form.header.mediaUrl.includes("{{") ? "text-[#008069]" : "text-[#111B21]"}`}>
                    {form.header.mediaUrl && form.header.mediaUrl.includes("{{")
                      ? `From ${getVariableNamesFromText(form.header.mediaUrl).map(v => `{{${v}}}`).join(", ") || "variable"}`
                      : (form.header.fileName || "Document")}
                  </span>
                </div>
              )}

              {/* Preview Body */}
              <div
                className="text-[11px] text-[#111B21] whitespace-pre-wrap break-words leading-[15px]"
                dangerouslySetInnerHTML={{ __html: getPreviewBody() || '<span class="text-[#8696a0]">Your message here...</span>' }}
              />

              {/* Preview Footer */}
              {form.footer.text && (
                <p className="text-[9px] text-[#667781] mt-1">{form.footer.text}</p>
              )}

              {/* Timestamp */}
              <div className="flex justify-end mt-0.5">
                <span className="text-[8px] text-[#667781]">
                  {new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}
                </span>
              </div>
            </div>

            {/* Preview Buttons */}
            {form.buttons.length > 0 && (
              <div className="border-t border-gray-100">
                {form.buttons.map((btn, i) => (
                  <div
                    key={btn.id}
                    className={`flex items-center justify-center gap-1 py-[5px] text-[10px] font-medium text-[#027EB5] ${
                      i < form.buttons.length - 1 ? "border-b border-gray-100" : ""
                    }`}
                  >
                    {btn.type === "URL" ? <ExternalLink size={9} /> : btn.type === "PHONE_NUMBER" ? <Phone size={9} /> : <MessageSquare size={9} />}
                    {btn.text || `Button ${i + 1}`}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Save/Cancel */}
      </NodeEditorTabs>
      <div className="flex gap-2 mt-3">
        <button
          onClick={handleSave}
          className="flex-1 py-2 text-[12px] font-bold bg-[#008069] text-white rounded-lg hover:bg-[#006e5a] transition-colors flex items-center justify-center gap-1.5 shadow-sm"
        >
          <Check size={14} />
          Save Template
        </button>
        <button
          onClick={onCancel}
          className="flex-1 py-2 text-[12px] font-bold bg-white text-[#667781] rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Main TemplateNode ────────────────────────────────────────────
const TemplateNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("template");

  const handleSave = useCallback(
    (formData) => {
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, {
          ...data,
          ...formData,
        });
      }
      closeEditor();
    },
    [id, data]
  );

  const handleCancel = useCallback(() => {
    closeEditor();
  }, []);

  const buttons = data.buttons || [];

  return (
    <div className={`relative transition-all shadow-md`} style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      {/* Target handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3.5 !h-3.5 !bg-[#008069] !border-2 !border-white !shadow-sm"
        style={{ left: -7 }}
      />

      {/* Content */}
      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          onCancel={handleCancel}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
          nodeId={id}
         presentation="fullscreen" onCancel={closeEditor} />
      </FlowNodeEditorShell>

      {/* Source handles - one per QUICK_REPLY button + "Other" handle */}
      {(() => {
        const quickReplyButtons = buttons.filter((btn) => btn.type === "QUICK_REPLY");
        if (quickReplyButtons.length > 0) {
          // total slots = quick-reply buttons + 1 "Other"
          const totalSlots = quickReplyButtons.length + 1;
          const handles = quickReplyButtons.map((btn, index) => {
            const pct = ((index + 1) / (totalSlots + 1)) * 100;
            return (
              <Handle
                key={btn.id}
                type="source"
                position={Position.Right}
                id={btn.id}
                style={{ top: `${pct}%`, right: -7 }}
                className={`!w-3 !h-3 !border-2 !border-white !shadow-sm ${
                  btn.connectedNodeId ? "!bg-[#008069]" : "!bg-[#027EB5]"
                }`}
                title={btn.text || btn.type}
              />
            );
          });
          // "Other" handle at the end
          const otherPct = ((quickReplyButtons.length + 1) / (totalSlots + 1)) * 100;
          handles.push(
            <Handle
              key="other"
              type="source"
              position={Position.Right}
              id="other"
              style={{ top: `${otherPct}%`, right: -7 }}
              className="!w-3 !h-3 !border-2 !border-white !shadow-sm !bg-[#F59E0B]"
              title="Other (no button match)"
            />
          );
          return handles;
        }
        return (
          <Handle
            type="source"
            position={Position.Right}
            id="default"
            className="!w-3.5 !h-3.5 !bg-[#008069] !border-2 !border-white !shadow-sm"
            style={{ right: -7 }}
          />
        );
      })()}
    </div>
  );
};

export default TemplateNode;
